import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { AnimatePresence, motion } from 'framer-motion';
import { Search, ChevronRight, Sparkles, RefreshCw } from 'lucide-react';
import Fuse from 'fuse.js';
import axios from 'axios';
import logger from '../../utils/logger';
import { API_URL } from '../../utils/apiConfig';
import { getUnitLabel } from '../../utils/unitLabels';
import { factorServable } from '../../utils/factorServable';

const normalize = (str) => typeof str === 'string' ? str.normalize('NFD').replace(/[\u0300-\u036f]/g, '') : str;

const FUSE_OPTIONS = {
  keys: [
    { name: 'name_simple_fr', weight: 4 },
    { name: 'name_simple_de', weight: 4 },
    { name: 'category_names_fr', weight: 2 },
    { name: 'category_names_de', weight: 2 },
    { name: 'source_product_name', weight: 1.5 },
    { name: 'subcategory', weight: 1.5 },
    { name: 'tags', weight: 1 },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 2,
  includeScore: true,
  getFn: (obj, path) => {
    const val = Fuse.config.getFn(obj, path);
    if (Array.isArray(val)) return val.map(v => normalize(v));
    return normalize(val);
  },
};

const GlobalFactorSearch = ({ isDark, showExpertFactors, onToggleExpert, onSelectFactor, onOpenCategory, disabled = false }) => {
  const { t, language } = useLanguage();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [truncatedSubs, setTruncatedSubs] = useState({});
  const [allFactors, setAllFactors] = useState(null);
  const [fuseAll, setFuseAll] = useState(null);
  const [fusePublic, setFusePublic] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setIsFocused(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadFactors = async () => {
    if (allFactors || isLoading) return;
    setIsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/emission-factors/search-index`);
      const data = res.data;
      setAllFactors(data);
      setFuseAll(new Fuse(data, FUSE_OPTIONS));
      setFusePublic(new Fuse(data.filter(f => factorServable(f, false)), FUSE_OPTIONS));
    } catch (err) {
      logger.error('Failed to load search index:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const fuseIndex = showExpertFactors ? fuseAll : fusePublic;
    if (!query || query.length < 2 || !fuseIndex) { setResults([]); setTruncatedSubs({}); return; }
    debounceRef.current = setTimeout(() => {
      const raw = fuseIndex.search(normalize(query), { limit: 300 });
      const normalizedQuery = normalize(query).toLowerCase();

      // --- Tier 1: Exact substring match in name ---
      const tier1 = [];
      const tier1Ids = new Set();
      for (const r of raw) {
        const f = r.item;
        const names = [f.name_simple_fr, f.name_simple_de, f.source_product_name].filter(Boolean);
        const isExact = names.some(n => normalize(n).toLowerCase().includes(normalizedQuery));
        if (isExact) {
          tier1.push(r);
          tier1Ids.add(f.id);
        }
      }

      // --- Tier 2: Fuzzy strict (score < 0.15), not already in Tier 1 ---
      const tier2 = [];
      const tier2Ids = new Set();
      for (const r of raw) {
        if (tier1Ids.has(r.item.id)) continue;
        if (r.score < 0.15) {
          tier2.push(r);
          tier2Ids.add(r.item.id);
        }
      }

      // --- Tier 3: Remaining (score 0.15–0.4), max 3 per subcategory ---
      const tier3 = [];
      const tier3SubCounts = {};
      const seenIds = new Set([...tier1Ids, ...tier2Ids]);
      for (const r of raw) {
        if (seenIds.has(r.item.id)) continue;
        const sub = r.item.subcategory || '_other';
        tier3SubCounts[sub] = (tier3SubCounts[sub] || 0) + 1;
        if (tier3SubCounts[sub] <= 3) tier3.push(r);
      }

      // --- Merge tiers, cap at 30 total ---
      const merged = [...tier1, ...tier2, ...tier3].slice(0, 30);

      // --- Compute truncated subcategories (links "+N") ---
      const subTotals = {};
      for (const r of raw) {
        const sub = r.item.subcategory || '_other';
        subTotals[sub] = (subTotals[sub] || 0) + 1;
      }
      const shownPerSub = {};
      for (const r of merged) {
        const sub = r.item.subcategory || '_other';
        shownPerSub[sub] = (shownPerSub[sub] || 0) + 1;
      }
      const truncated = {};
      for (const [sub, total] of Object.entries(subTotals)) {
        const shown = shownPerSub[sub] || 0;
        if (total > shown) {
          const sample = raw.find(r => (r.item.subcategory || '_other') === sub)?.item;
          truncated[sub] = {
            label_fr: sample?.subcategory_name_fr || sub,
            label_de: sample?.subcategory_name_de || sub,
            hidden: total - shown,
            subcategory: sub,
          };
        }
      }
      setTruncatedSubs(truncated);
      setResults(merged);
    }, 150);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, fuseAll, fusePublic, showExpertFactors]);

  const handleSelect = (factor) => { setQuery(''); setResults([]); setIsFocused(false); onSelectFactor(factor); };

  const getDisplayName = (f) => {
    if (language === 'fr') return f.name_simple_fr || f.source_product_name || '—';
    return f.name_simple_de || f.source_product_name || '—';
  };

  const getImpactText = (f) => {
    if (!f.impact) return '';
    const v = f.impact.value;
    if (v == null) return '';
    return `${v >= 0.01 ? v.toFixed(4) : v.toExponential(2)} ${f.impact.unit || ''}`;
  };

  return (
    <div ref={containerRef} className={`relative ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} data-testid="global-factor-search"
         title={disabled ? t('dataEntry.actions.readonlyTooltip') : undefined}>
      <div className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-all ${
        isFocused
          ? (isDark ? 'border-blue-500 bg-slate-800 ring-2 ring-blue-500/20' : 'border-blue-400 bg-white ring-2 ring-blue-400/20')
          : (isDark ? 'border-slate-700 bg-slate-800/50' : 'border-gray-200 bg-white')
      }`}>
        <Search className={`w-5 h-5 flex-shrink-0 ${isFocused ? 'text-blue-500' : (isDark ? 'text-slate-500' : 'text-gray-400')}`} />
        <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
          onFocus={() => { if (disabled) return; setIsFocused(true); loadFactors(); }}
          placeholder={t('dataEntry.globalSearchPlaceholder')}
          disabled={disabled}
          className={`flex-1 bg-transparent outline-none text-sm ${isDark ? 'text-white placeholder-slate-500' : 'text-gray-900 placeholder-gray-400'} ${disabled ? 'cursor-not-allowed' : ''}`}
          data-testid="global-search-input" />
        {isLoading && <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />}
        <div className="flex items-center gap-2 flex-shrink-0 ml-2 pl-2 border-l border-slate-600/30">
          <button onClick={onToggleExpert} data-testid="expert-toggle-btn"
            className={`relative flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
              showExpertFactors ? (isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700')
                : (isDark ? 'bg-slate-700 text-slate-400 hover:text-slate-300' : 'bg-gray-100 text-gray-500 hover:text-gray-700')
            }`}>
            <Sparkles className="w-3 h-3" />{language === 'fr' ? 'Expert' : 'Experte'}
            <div className={`w-6 h-3.5 rounded-full transition-colors ${showExpertFactors ? 'bg-amber-500' : (isDark ? 'bg-slate-600' : 'bg-gray-300')}`}>
              <div className={`w-2.5 h-2.5 rounded-full bg-white mt-0.5 transition-transform ${showExpertFactors ? 'translate-x-3' : 'translate-x-0.5'}`} />
            </div>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isFocused && (query.length >= 2 || isLoading) && (
          <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }}
            className={`absolute left-0 right-0 top-full mt-2 z-50 rounded-xl shadow-2xl border overflow-hidden ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}
            data-testid="search-results-dropdown">
            {isLoading ? (
              <div className={`px-4 py-6 text-center text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin text-blue-500" />{t('dataEntry.loadingIndex')}
              </div>
            ) : results.length === 0 && query.length >= 2 ? (
              <div className={`px-4 py-6 text-center text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                {t('dataEntry.search.noResults').replace('{query}', query)}{!showExpertFactors ? t('dataEntry.search.expertHint') : ''}
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto">
                {results.map((r, idx) => {
                  const f = r.item;
                  return (
                    <button key={f.id} onClick={() => handleSelect(f)} data-testid={`search-result-${idx}`}
                      className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors border-b last:border-b-0 ${isDark ? 'border-slate-700/50 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-blue-50/50'}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{getDisplayName(f)}</p>
                          {!f.is_public && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isDark ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700'}`}>Expert</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{f.subcategory || f.category}</span>
                          {f.default_unit && <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>{getUnitLabel(f.default_unit, language)}</span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className={`text-xs font-mono ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{getImpactText(f)}</p>
                      </div>
                      <ChevronRight className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                    </button>
                  );
                })}
                {Object.entries(truncatedSubs).map(([sub, info]) => (
                  <button key={`more-${sub}`} data-testid={`search-more-${sub}`}
                    onClick={() => { setQuery(''); setResults([]); setTruncatedSubs({}); setIsFocused(false); onOpenCategory?.(sub, query); }}
                    className={`w-full text-left px-4 py-2.5 flex items-center gap-2 transition-colors border-b last:border-b-0 ${
                      isDark ? 'border-slate-700/50 hover:bg-blue-500/10' : 'border-gray-100 hover:bg-blue-50'
                    }`}>
                    <span className={`text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                      {t('dataEntry.searchMoreResults').replace('{count}', info.hidden).replace('{category}', language === 'fr' ? info.label_fr : info.label_de)}
                    </span>
                    <ChevronRight className={`w-3.5 h-3.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GlobalFactorSearch;
